<script lang="ts">
    import 'pdfjs-dist/web/pdf_viewer.css'; // CSS for text and annotation layers

    import type * as Pdfjs from 'pdfjs-dist/types/src/pdf';
    import type {ViewableFile} from 'typings/portaro.be.types';
    import type {Unsubscriber} from 'svelte/store';
    import type {PdfjsModule, PdfRenderedPage} from 'src/features/media-viewer/services/mv-pdf-render.service';
    import type {Subscription} from 'rxjs';
    import {get} from 'svelte/store';
    import {clickOutside} from 'shared/svelte-actions/use.click-outside';
    import {createEventDispatcher, onDestroy, onMount} from 'svelte';
    import {getViewedContentContext} from '../../../lib/mv-viewed-content';
    import {calculateOptimalPdfPageScaling, calculateSidePreviewVisibility} from './utils';
    import {getMediaViewerContext} from '../../../lib/mv-context';
    import {getLogger} from 'core/svelte-context/context';
    import {range} from 'shared/utils/array-utils';
    import {MvPdfRenderService} from 'src/features/media-viewer/services/mv-pdf-render.service';
    import {unsubscribeAllSubscriptions} from 'shared/utils/observables-utils';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import {fly} from 'svelte/transition';
    import {CONTENT_SCROLL_EVENT, GO_TO_PAGE_EVENT, SCALE_TO_FIT_EVENT} from '../../../lib/mv-constants';
    import MvPdfSidePreview from './mv-pdf-side-preview/MvPdfSidePreview.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import MvPdfPage from './mv-pdf-page/MvPdfPage.svelte';
    import MvPlaceholderPdfPage from './mv-pdf-page/MvPlaceholderPdfPage.svelte';
    import MvPdfPasswordPrompt from './MvPdfPasswordPrompt.svelte';
    import MvFileTextualForm from 'src/features/media-viewer/components/mv-viewed-content/MvFileTextualForm.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import {
        isCurrentLayout,
        isCurrentLayoutOrBigger,
        MediaViewerLayoutSize,
        mediaViewerLayoutWidth
    } from '../../../lib/mv-layout';

    export let file: ViewableFile;

    const logger = getLogger();
    const context = getMediaViewerContext();
    const viewedContent = getViewedContentContext();
    const CMAP_URL = 'pdfjs-dist/cmaps/';
    const contentFlyInAnimParams = {y: 10, duration: 250};
    const dispatch = createEventDispatcher<{
        'load-start': void,
        'load-success': void,
        'load-error': void
    }>();

    // PDF props
    let loading = true;
    let pdfJsModule: PdfjsModule;
    let loadingTask: Pdfjs.PDFDocumentLoadingTask | undefined;
    let pdfDocument: Pdfjs.PDFDocumentProxy;
    let defaultPdfViewport: Pdfjs.PageViewport;
    let scaledPdfViewport: Pdfjs.PageViewport;
    let pdfRenderService: MvPdfRenderService;
    let renderedPages: Record<number, PdfRenderedPage> = {};
    let renderedPagesSubscription: Subscription;

    // PDF password props
    let requirePassword = false;
    let wrongPassword = false;
    let newPasswordCallback: ((password: string) => void) | undefined;

    // Viewer props
    let viewerViewport = get(viewedContent.viewport);
    let viewerZoom = get(viewedContent.zoom);
    let pageData = get(viewedContent.pageData);
    let showingTextualInformation = get(viewedContent.showingTextualForm);
    let pdfViewerElement: HTMLDivElement;
    let pagesContainerElement: HTMLDivElement;
    let viewerViewportUnsubscribe: Unsubscriber;
    let viewerZoomUnsubscribe: Unsubscriber;
    $: previewPagesOverflowing = calculateSidePreviewVisibility(viewerViewport, scaledPdfViewport);

    const pageDataUnsubscribe = viewedContent.pageData.subscribe((currentPageData) => {
        pageData = currentPageData;

        if (exists(pageData) && exists(context.urlManagementService)) {
            context.urlManagementService.setPageNumber(pageData.current);
        }
    });
    const showingTextualInformationUnsubscribe = viewedContent.showingTextualForm.subscribe((textualForm) => showingTextualInformation = textualForm);
    const urlStateSubscription = exists(context.urlManagementService) ? context.urlManagementService.getUrlState$().subscribe((state) => {
        const currentPageData = get(viewedContent.pageData);

        if (exists(state.page) && state.page !== currentPageData?.current?.toString()) {
            const pageNumber = Number(state.page);

            if (!isNaN(pageNumber)) {
                waitForPdfLoad().then(() => viewedContent.eventBus.dispatchEvent(new CustomEvent<number>(GO_TO_PAGE_EVENT, {detail: pageNumber})));
            }
        }
    }) : undefined;

    onMount(async () => {
        try {
            // Load PDF.JS module and initialize loading task and load PDF document proxy
            pdfJsModule = await import(/* webpackChunkName: "pdfjs-dist" */ 'pdfjs-dist/webpack.mjs');
            pdfDocument = await startLoadingDocument(`/files/${file.id}`);

            // PDF document proxy loaded successfully
            loading = false;
            dispatch('load-success');

            // Initialize PDF viewport and default zoom value
            const firstPage = await pdfDocument.getPage(1);
            defaultPdfViewport = firstPage.getViewport({scale: 1});
            const optimalZoom = calculateOptimalPdfPageScaling(defaultPdfViewport, viewerViewport);
            viewedContent.setZoom(optimalZoom);

            scaledPdfViewport = firstPage.getViewport({scale: optimalZoom});

            // Initialize viewer page data
            viewedContent.setPageData({
                current: 1,
                total: pdfDocument.numPages,
                pagesShown: false
            });

            // Subscribe to changes to viewport and zoom
            viewerViewportUnsubscribe = viewedContent.viewport.subscribe((currentViewport) => viewerViewport = currentViewport);
            viewerZoomUnsubscribe = viewedContent.zoom.subscribe((currentZoom) => {
                viewerZoom = currentZoom;
                scaledPdfViewport = firstPage.getViewport({scale: currentZoom});
            });

            pdfRenderService = new MvPdfRenderService(logger, pdfDocument, defaultPdfViewport, viewedContent);
            renderedPagesSubscription = pdfRenderService.getRenderedPages$().subscribe((renderedPagesData) => renderedPages = renderedPagesData);
            queuePageRender(range(1, Math.min(10, pdfDocument.numPages)));

            // Register event listeners
            viewedContent.eventBus.addEventListener(GO_TO_PAGE_EVENT, handleGoToPage);
            viewedContent.eventBus.addEventListener(SCALE_TO_FIT_EVENT, handleScaleToFit);
        } catch (e) {
            logger.error(e);

            dispatch('load-error');
            loading = false;
        }
    });

    onDestroy(async () => {
        // Cancel ongoing PDF loading tasks and its worker
        if (exists(loadingTask) && exists(loadingTask._worker) && loadingTask._worker && !loadingTask._worker.destroyed) {
            loadingTask._worker.destroy();
        }

        if (exists(loadingTask) && !loadingTask.destroyed) {
            await loadingTask.destroy();
        }

        unsubscribeAllSubscriptions(viewerViewportUnsubscribe, showingTextualInformationUnsubscribe, viewerZoomUnsubscribe, pageDataUnsubscribe, renderedPagesSubscription, urlStateSubscription);

        // Unsubscribe all subscriptions and event handlers
        viewedContent.setPageData(null);
        viewedContent.eventBus.removeEventListener(GO_TO_PAGE_EVENT, handleGoToPage);
        viewedContent.eventBus.removeEventListener(SCALE_TO_FIT_EVENT, handleScaleToFit);
    });

    // Starts loading PDF document from provided source
    function startLoadingDocument(fileUrl: string): Promise<Pdfjs.PDFDocumentProxy> {
        loadingTask = pdfJsModule.getDocument({
            url: fileUrl,
            rangeChunkSize: 1048576 * 4, // 4MB chunk size
            disableStream: true, // Force only range requests,
            disableAutoFetch: true,
            cMapPacked: true,
            cMapUrl: CMAP_URL
        });

        loadingTask.onPassword = (newPassword: (value: string) => void, reason: number) => {
            wrongPassword = reason === 2; // reason equals 2 means that the password is wrong
            requirePassword = true;
            newPasswordCallback = newPassword;
        };

        dispatch('load-start');

        return loadingTask.promise;
    }

    function queuePageRender(pageNumber: number | number[]): void {
        if (Array.isArray(pageNumber)) {
            pageNumber.map((page) => queuePageRender(page));
            return;
        }

        pdfRenderService.queuePageForRender(pageNumber);
    }

    // Wait for once the PDF document is loaded
    function waitForPdfLoad(): Promise<void> {
        return new Promise((resolve) => {
            if (!loading) {
                resolve();
            }

            const interval = setInterval(() => {
                if (!loading) {
                    clearInterval(interval);
                    resolve();
                }
            }, 100);
        });
    }

    const handleGoToPage = (event: CustomEvent<number>) => {
        const pdfPageElement = document.getElementById(`mv-pdf-page-${event.detail}`);
        if (!exists(pdfPageElement)) {
            return;
        }

        pdfPageElement.scrollIntoView({
            behavior: 'instant',
            block: 'center',
            inline: 'center'
        });
    };

    const handleScaleToFit = () => {
        viewedContent.setZoom(calculateOptimalPdfPageScaling(defaultPdfViewport, viewerViewport));
    };

    const handleClickOutside = () => {
        if (isCurrentLayout(MediaViewerLayoutSize.MOBILE, $mediaViewerLayoutWidth) && pageData?.pagesShown === true) {
            viewedContent.setPageData({
                ...pageData,
                pagesShown: false
            });
        }
    };

    const handlePagesScroll = () => {
        if (!exists(pdfViewerElement) || !exists(pagesContainerElement)) {
            return;
        }

        viewedContent.eventBus.dispatchEvent(new CustomEvent<number>(
            CONTENT_SCROLL_EVENT,
            {detail: pdfViewerElement.scrollTop}
        ));

        const children = Array.from(pagesContainerElement.children) as HTMLElement[];
        const viewportMiddle = window.innerHeight / 2;

        let closestChild: HTMLElement | null = null;
        let closestDistance = Infinity;

        // Find the child element closest to the center of the viewport
        for (const child of children) {
            const rect = child.getBoundingClientRect();
            const childMiddle = rect.top + rect.height / 2;
            const distance = Math.abs(viewportMiddle - childMiddle);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestChild = child;
            }
        }

        if (exists(closestChild)) {
            const pageNumber = parseInt(closestChild.id.replace('mv-pdf-page-', ''), 10);
            const currentPageData = get(viewedContent.pageData);

            if (currentPageData.current !== pageNumber && !isNaN(pageNumber)) {
                viewedContent.setPageData({
                    ...currentPageData,
                    current: pageNumber
                });
            }
        }
    };

    const handleToggleSidePreviewPagesClick = () => {
        const currentPageData = get(viewedContent.pageData);
        viewedContent.setPageData({...currentPageData, pagesShown: !currentPageData.pagesShown});
    };

    const handlePdfPasswordSubmit = (event: CustomEvent<string>) => {
        if (!isFunction(newPasswordCallback)) {
            return;
        }

        newPasswordCallback(event.detail);
        requirePassword = false;
    };
</script>

{#if loading || !exists(pdfDocument)}
    <div class="loading-container">
        <KpLoadingBlock/>
    </div>
{/if}

{#if requirePassword}
    <div class="password-prompt-container">
        <MvPdfPasswordPrompt {wrongPassword} on:password-submit={handlePdfPasswordSubmit}/>
    </div>
{/if}

{#if !loading && exists(pdfDocument) && exists(pdfRenderService) && exists(scaledPdfViewport)}
    <div class="mv-pdf-viewer"
         bind:this={pdfViewerElement}
         on:scroll={handlePagesScroll}>

        {#if !showingTextualInformation}
            <div class="pages-container"
                 bind:this={pagesContainerElement}
                 style:gap="{24 * viewerZoom}px"
                 in:fly={contentFlyInAnimParams}>

                {#each range(1, pdfDocument.numPages) as i}
                    {#if exists(renderedPages[i])}
                        <MvPdfPage pageNumber="{i}"
                                   renderedPage="{renderedPages[i]}"
                                   width="{scaledPdfViewport.width}"
                                   height="{scaledPdfViewport.height}"/>
                    {:else}
                        <MvPlaceholderPdfPage pageNumber="{i}"
                                              width="{scaledPdfViewport.width}"
                                              height="{scaledPdfViewport.height}"
                                              {pdfRenderService}/>
                    {/if}
                {/each}
            </div>
        {/if}

        {#if showingTextualInformation}
            <div class="pages-container">
                <MvFileTextualForm {file}/>
            </div>
        {/if}
    </div>
{/if}

{#if !loading && exists(pdfDocument) && exists(scaledPdfViewport) && exists(pdfRenderService) && (!showingTextualInformation || !file.hasTextualForm) && context.viewerType === 'media-viewer'}
    <div class="side-preview-container"
         use:clickOutside
         on:click-outside={handleClickOutside}
         class:pages-hidden={previewPagesOverflowing && !pageData.pagesShown}>

        <MvPdfSidePreview totalPages="{pdfDocument.numPages}"
                          heightWidthRatio="{scaledPdfViewport.height / scaledPdfViewport.width}" {pdfRenderService}/>
    </div>

    {#if isCurrentLayoutOrBigger(MediaViewerLayoutSize.TABLET, $mediaViewerLayoutWidth)}
        <button class="side-preview-pages-button"
                type="button"
                class:button-shown-on-side={previewPagesOverflowing && !pageData.pagesShown}
                class:button-shown-fully={previewPagesOverflowing && pageData.pagesShown}
                on:click={handleToggleSidePreviewPagesClick}>

            <div class="icon-container">
                <UIcon icon="angle-small-left"/>
            </div>
        </button>
    {/if}
{/if}

<style lang="less">
    @import (reference) "src/features/media-viewer/media-viewer.variables.less";

    .mv-pdf-viewer {
        position: absolute;
        display: flex;
        flex-direction: column;
        overflow-y: scroll;
        padding: @spacing-ml;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--viewer-content-bg);

        .pages-container {
            margin-left: auto;
            margin-right: auto;
            display: flex;
            flex-direction: column;
        }
    }

    .password-prompt-container,
    .loading-container {
        position: absolute;
        z-index: 2;
        top: 47.5%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .side-preview-container {
        position: absolute;
        top: 0;
        bottom: 0;
        right: @spacing-s;
        overflow-y: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;
        padding: @spacing-ml @spacing-l @spacing-ml 3px;
        transition: transform 0.4s ease-in-out;
        animation: 0.4s ease-in-out 0s 1 sidebar-slide-from-left;

        &.pages-hidden {
            transform: translateX(calc(100% + 20px));
            animation: none;
        }

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .side-preview-pages-button {
        position: absolute;
        top: 50%;
        right: 0;
        width: 40px;
        height: 36px;
        border-radius: @border-radius-default;
        background-color: var(--viewer-default-button-color);
        border: 1px solid var(--viewer-default-border);
        display: flex;
        align-items: center;
        justify-content: center;
        transform: translate(100%, -50%);
        transition: transform 0.4s ease-in-out, background-color 0.3s ease-in-out;

        &.button-shown-on-side {
            transform: translate(4px, -50%);
        }

        &.button-shown-fully {
            transform: translate(-134px, -50%);

            .icon-container {
                rotate: -180deg;
            }
        }

        .icon-container {
            transition: transform 0.3s ease-in-out, rotate 0.3s ease-in-out;
        }

        &:hover {
            background-color: var(--viewer-blue-highlight);

            .icon-container {
                transform: translateX(-2px);
            }
        }
    }

    @keyframes preview-slide-from-right {
        0% {
            transform: translateX(25%);
            opacity: 0;
        }
        100% {
            transform: translateX(0);
            opacity: 100%;
        }
    }
</style>